import React, { useState } from "react";
import { Box, styled } from "@mui/material";
import { JobGearHeader } from "./JobGearHeader";
import { JobGearCoverContent } from "./JobGearCoverContent";
import { JobGearItemContent } from "./JobGearItemContent";
import { JOB_GEAR_XP } from "../../../constants";
import type { JobGearActivityProps } from "../../../types";

const JobGearContainer = styled(Box, {
  label: "JobGearContainer",
})(({ theme }) => ({
  position: "fixed",
  margin: 0,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "#1c252e",
  zIndex: theme.zIndex.modal,
  display: "flex",
  flexDirection: "column",
  overflowX: "hidden",
  overflowY: "auto",
}));

const ContentArea = styled(Box, {
  label: "JobGearContentArea",
})(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "visible",
  maxWidth: "1280px",
  margin: "0 auto",
  width: "100%",
}));

export const JobGearActivity = ({
  onClose,
  hat,
  activityCompletion,
  onComplete,
  onXPEarned,
}: JobGearActivityProps) => {
  const xpValue = JOB_GEAR_XP;
  const [currentStep, setCurrentStep] = useState(-1);

  const jobGearItems = hat.msJobGear || [];
  const totalSteps = jobGearItems.length;

  const handleCompleted = () => {
    if (!activityCompletion) {
      onComplete?.();
      onXPEarned?.(xpValue, `jobgear-${hat.id}`);
    }
  };

  const getProgress = () => {
    if (currentStep === -1) return 0;
    return ((currentStep + 1) / totalSteps) * 100;
  };

  const handleStart = () => {
    if (jobGearItems.length === 0) {
      handleCompleted();
      onClose();
      return;
    }
    setCurrentStep(0);
  };

  const handleNext = () => {
    if (currentStep === jobGearItems.length - 1) {
      handleCompleted();
    }

    if (currentStep < jobGearItems.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else if (currentStep === 0) {
      setCurrentStep(-1);
    }
  };

  const renderContent = () => {
    if (currentStep === -1) {
      return (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 3,
          }}
        >
          <JobGearCoverContent
            hat={hat}
            onStart={handleStart}
            activityCompletion={activityCompletion}
            xpValue={xpValue}
          />
        </Box>
      );
    } else {
      const currentItem = jobGearItems[currentStep];
      if (!currentItem) {
        return (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 3,
            }}
          >
            <JobGearCoverContent
              hat={hat}
              onStart={handleStart}
              activityCompletion={activityCompletion}
              xpValue={xpValue}
            />
          </Box>
        );
      }

      return (
        <JobGearItemContent
          item={currentItem}
          currentIndex={currentStep}
          totalItems={jobGearItems.length}
          onNext={handleNext}
          onPrevious={handlePrevious}
        />
      );
    }
  };

  return (
    <JobGearContainer>
      <JobGearHeader onClose={onClose} progress={getProgress()} />
      <ContentArea>{renderContent()}</ContentArea>
    </JobGearContainer>
  );
};
